
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DEMO_ORDERS } from '@/utils/demoData';
import { 
  getCachedOrders, 
  setCachedOrders, 
  clearCache, 
  shouldFetchOrders
} from '@/utils/etsyCache';

export interface OrderItem {
  title: string;
}

export interface EtsyOrder {
  receiptId: string;
  trackingId: string;
  customerName: string;
  productName: string;
  items?: OrderItem[];
  city: string;
  zip: string;
  address: string;
  country: string; // Add country field
  date: string;
  status: string;
  totalAmount?: number;
  subtotalAmount?: number;
  currencyCode?: string;
  customerEmail?: string;
}

export const useEtsyOrders = (hasActiveSubscription: boolean, isLoading: boolean) => {
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [etsyOrders, setEtsyOrders] = useState<EtsyOrder[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (hasActiveSubscription && !isLoading) {
      const cachedOrders = getCachedOrders();
      if (cachedOrders.length > 0) {
        setEtsyOrders(cachedOrders);
      }
    } else if (!hasActiveSubscription && !isLoading) {
      setEtsyOrders(DEMO_ORDERS);
    }
  }, [hasActiveSubscription, isLoading]);

  const fetchEtsyOrders = async (forceRefresh = false) => {
    if (!hasActiveSubscription) {
      setEtsyOrders(DEMO_ORDERS);
      return;
    }

    if (!forceRefresh && !shouldFetchOrders()) {
      const cachedOrders = getCachedOrders();
      setEtsyOrders(cachedOrders);
      return;
    }

    setIsFetchingOrders(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-etsy-receipts');
      if (error) throw error;

      const orders = data.orders || [];
      setEtsyOrders(orders);
      setCachedOrders(orders);

      toast({
        title: "Orders Updated",
        description: `Fetched ${data.count} orders from Etsy`,
      });

    } catch (error: any) {
      console.error('Error fetching Etsy orders:', error);
      toast({
        title: "Fetch Failed",
        description: error.message || "Failed to fetch orders from Etsy",
        variant: "destructive",
      });
    } finally {
      setIsFetchingOrders(false);
    }
  };

  const syncEtsyOrders = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "Demo Mode",
        description: "You're viewing demo data. Upgrade to sync your actual Etsy orders.",
        variant: "destructive",
      });
      return;
    }
    await fetchEtsyOrders(true);
  };

  return {
    fetchEtsyOrders,
    syncEtsyOrders,
    isFetchingOrders,
    etsyOrders,
    setEtsyOrders,
  };
};
